/**
 * Arabic-aware text normalization for client-side MATCHING (city names, search
 * suggestions, typed numbers). These helpers build comparison keys only — never
 * rewrite text that is shown to the user with them.
 *
 * `normalizeArabic` mirrors the backend's canonical transform
 * (backend/server/lib/search/arabic.js, itself kept identical to the SQL
 * `normalize_arabic()`), so «جده», «جدة» and «جدّة» compare equal on both sides.
 * If a rule changes there, change it here too.
 */

// Tashkeel (harakat, tanween, shadda, sukun, Quranic marks), superscript alef and
// tatweel — the same character class as the backend STRIP_RE.
const ARABIC_MARKS_RE = /[ؐ-ًؚ-ٰٟـ]/g;
const ARABIC_INDIC_DIGIT_RE = /[٠-٩]/g;
const EASTERN_ARABIC_DIGIT_RE = /[۰-۹]/g;

/** آ أ إ ٱ → ا · ة → ه · ى → ي · ؤ → و · ئ → ي (same letter folds as the backend). */
function mapLetters(text: string): string {
  return text
    .replace(/[آأإٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي');
}

/** Arabic-Indic (٠-٩) and Eastern-Arabic/Persian (۰-۹) digits → ASCII 0-9. */
function mapDigits(text: string): string {
  return text
    .replace(ARABIC_INDIC_DIGIT_RE, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(EASTERN_ARABIC_DIGIT_RE, (d) => String(d.charCodeAt(0) - 0x06f0));
}

/**
 * Western digits for anything a user typed or pasted: ٠-٩ / ۰-۹ → 0-9, the Arabic
 * decimal separator ٫ → ".", the Arabic thousands separator ٬ → "," and ٪ → "%".
 * Everything else is left untouched.
 */
export function toLatinDigits(text: string): string {
  return mapDigits(text).replace(/٫/g, '.').replace(/٬/g, ',').replace(/٪/g, '%');
}

/**
 * Canonical Arabic normalization (backend parity): strips tashkeel and tatweel,
 * unifies alef / hamza / ta-marbuta / alef-maqsura variants, folds digits to ASCII
 * and collapses whitespace. Latin text passes through except for whitespace.
 */
export function normalizeArabic(text: string | null | undefined): string {
  if (text === null || text === undefined) return '';
  let s = String(text).replace(ARABIC_MARKS_RE, '');
  s = mapLetters(s);
  s = mapDigits(s);
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Comparison key for names and search input: `normalizeArabic` plus Latin
 * case-folding, Latin accents removed, apostrophes dropped ("Ha'il" → "hail") and
 * any other punctuation read as a space ("Al-Khobar" → "al khobar", «جدة،» → «جده»).
 */
export function toSearchKey(text: string | null | undefined): string {
  if (text === null || text === undefined) return '';
  // NFD splits accented Latin letters AND hamza-carrying Arabic letters into
  // base + combining mark; dropping the marks gives the same result as the
  // letter folds above (أ → ا, ؤ → و, ئ → ي), so both paths agree.
  let s = String(text).normalize('NFD').replace(/\p{M}/gu, '');
  s = normalizeArabic(s).toLowerCase();
  s = s.replace(/['’‘`´ʻʼʿʾ]/g, '');
  s = s.replace(/[^\p{L}\p{N}]+/gu, ' ');
  return s.replace(/\s+/g, ' ').trim();
}

const ARABIC_ARTICLE_RE = /^ال(?=\S{2,})/;
const LATIN_ARTICLE_RE = /^(?:al|el|ad|adh|ar|as|ash|at|ath|az|an)\s+(?=\S)/;

/**
 * Drops a leading definite article from a `toSearchKey` result: Arabic «ال»
 * («الخبر» → «خبر») or a transliterated one ("al khobar" → "khobar",
 * "at taif" → "taif", "ar rass" → "rass"). Returns the key unchanged otherwise.
 */
export function stripDefiniteArticle(key: string): string {
  return key.replace(ARABIC_ARTICLE_RE, '').replace(LATIN_ARTICLE_RE, '');
}
